import React, { useState, useEffect } from 'react';
import { getPhotoStudioAppointments } from '../services/photoStudioService';
import socketService from '../services/socketService';

const RealTimeQueue = ({ studioCode = 'BLR13' }) => {
  const [queueData, setQueueData] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    currentToken: null,
    nextTokens: []
  });
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const updateQueueStatus = async () => {
    try {
      const result = await getPhotoStudioAppointments();
      if (result.success) {
        const today = new Date().toDateString();
        const studioAppointments = result.data.filter(apt => 
          apt.siteCode === studioCode && 
          new Date(apt.submissionDate || apt.createdAt).toDateString() === today
        );

        const pending = studioAppointments.filter(apt => apt.status === 'Pending').length;
        const inProgress = studioAppointments.filter(apt => apt.status === 'In Progress').length;
        const completed = studioAppointments.filter(apt => apt.status === 'Completed').length;
        
        const currentToken = studioAppointments.find(apt => apt.status === 'In Progress')?.tokenNumber || null;
        const nextTokens = studioAppointments
          .filter(apt => apt.status === 'Pending')
          .sort((a, b) => (a.queuePosition || 0) - (b.queuePosition || 0))
          .slice(0, 5)
          .map(apt => apt.tokenNumber);

        const newQueueData = {
          pending,
          inProgress,
          completed,
          currentToken,
          nextTokens
        };

        setQueueData(newQueueData);
        setLastUpdate(new Date());

        // Emit queue update via socket
        socketService.emitQueueUpdate(studioCode, newQueueData);
      }
    } catch (error) {
      console.error('Error updating queue status:', error);
    }
  };

  useEffect(() => {
    updateQueueStatus();
    
    // Connect to socket
    socketService.connect();
    socketService.joinStudioRoom(studioCode);
    
    // Listen for real-time updates
    socketService.onQueueUpdate((data) => {
      if (data.studioCode === studioCode) {
        setQueueData(data.queueData);
        setLastUpdate(new Date());
      }
    });

    // Update every 30 seconds
    const interval = setInterval(updateQueueStatus, 30000);

    return () => {
      clearInterval(interval);
      socketService.leaveStudioRoom(studioCode);
    };
  }, [studioCode]);

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '20px',
      margin: '20px 0'
    }}>
      <h3 style={{ color: '#495057', marginBottom: '20px' }}>
        🔴 Live Queue Status - {studioCode}
      </h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px' }}>
        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '6px' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404' }}>{queueData.pending}</div>
          <div style={{ fontSize: '14px', color: '#856404' }}>Pending</div>
        </div>
        
        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#d1ecf1', borderRadius: '6px' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0c5460' }}>{queueData.inProgress}</div>
          <div style={{ fontSize: '14px', color: '#0c5460' }}>In Progress</div>
        </div>
        
        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#d4edda', borderRadius: '6px' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#155724' }}>{queueData.completed}</div>
          <div style={{ fontSize: '14px', color: '#155724' }}>Completed</div>
        </div>
      </div>

      {queueData.currentToken && (
        <div style={{ 
          backgroundColor: '#007bff', 
          color: 'white', 
          padding: '15px', 
          borderRadius: '6px', 
          textAlign: 'center',
          marginBottom: '15px'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
            📸 Current: {queueData.currentToken}
          </div>
        </div>
      )}

      {queueData.nextTokens.length > 0 && (
        <div>
          <h4 style={{ color: '#495057', marginBottom: '10px' }}>⏳ Next in Queue:</h4>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {queueData.nextTokens.map((token, index) => (
              <span key={token} style={{
                backgroundColor: '#e9ecef',
                padding: '8px 12px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#495057'
              }}>
                {index + 1}. {token}
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ 
        fontSize: '12px', 
        color: '#6c757d', 
        textAlign: 'right', 
        marginTop: '15px' 
      }}>
        Last updated: {lastUpdate.toLocaleTimeString()}
      </div>
    </div>
  );
};

export default RealTimeQueue;